import { useEffect, useState } from 'react';
import { MEMO_MAX, type CandidatureDetail } from '@rh/shared';
import { appel } from '../api';

/**
 * Petit mot collé à droite du nom du candidat (demande du 2026-09-19 : d'abord
 * devant le nom, puis « placer mot à droite » — devant, il décalait les noms
 * d'une carte à l'autre), dans la liste comme sur la fiche. Il se modifie là où il s'affiche : un clic ouvre
 * le champ, Entrée ou la sortie du champ enregistre, Échap abandonne.
 *
 * À ne pas confondre avec l'**étiquette de profil** (migration 018), qui dit
 * le métier à côté de la pastille : celui-ci dit ce qu'on veut retenir du
 * dossier — « à rappeler lundi », « recommandée par le chef d'atelier ».
 * Comme partout : ni âge, ni sexe, ni situation familiale, ni santé.
 *
 * Sans droit d'écriture (rôle lecteur, fiche anonymisée), le mot s'affiche
 * mais ne s'ouvre pas ; vide, il ne montre rien du tout.
 */
export function MemoCandidat({
  candidatureId,
  memo,
  nom,
  modifiable,
  onChange,
}: {
  candidatureId: string;
  memo: string | null;
  /** Nom du candidat, pour les libellés lus à voix haute. */
  nom: string;
  modifiable: boolean;
  onChange: (fiche: CandidatureDetail) => void;
}) {
  const [edition, setEdition] = useState(false);
  const [texte, setTexte] = useState(memo ?? '');
  const [envoi, setEnvoi] = useState(false);

  // Le mot venu du serveur reprend la main (relecture, envoi d'un autre champ).
  useEffect(() => setTexte(memo ?? ''), [memo]);

  if (!modifiable) {
    return memo ? <span className="memo-candidat">{memo}</span> : null;
  }

  async function enregistrer(): Promise<void> {
    const propre = texte.trim().slice(0, MEMO_MAX);
    setEdition(false);
    // Rien n'a changé : pas d'appel, pas de ligne au journal.
    if (propre === (memo ?? '')) {
      setTexte(memo ?? '');
      return;
    }
    setEnvoi(true);
    try {
      const fiche = await appel<CandidatureDetail>(`/candidatures/${candidatureId}/memo`, {
        method: 'PUT',
        body: JSON.stringify({ memo: propre || null }),
      });
      onChange(fiche);
    } catch {
      // Envoi refusé ou réseau coupé : on remet ce que le serveur avait.
      setTexte(memo ?? '');
    } finally {
      setEnvoi(false);
    }
  }

  if (edition) {
    return (
      <input
        className="memo-candidat champ-memo"
        value={texte}
        maxLength={MEMO_MAX}
        autoFocus
        disabled={envoi}
        aria-label={`Petit mot à côté du nom de ${nom}`}
        onChange={(evenement) => setTexte(evenement.target.value)}
        onBlur={() => void enregistrer()}
        onKeyDown={(evenement) => {
          if (evenement.key === 'Enter') {
            evenement.preventDefault();
            void enregistrer();
          }
          if (evenement.key === 'Escape') {
            setTexte(memo ?? '');
            setEdition(false);
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className={`memo-candidat${memo ? '' : ' memo-vide'}`}
      aria-label={memo ? `Modifier le mot « ${memo} » de ${nom}` : `Coller un mot à côté de ${nom}`}
      onClick={() => setEdition(true)}
    >
      {memo ?? '+ mot'}
    </button>
  );
}
